import { confirmDialog } from "./confirmDialog";
import {
  cashFlowCategoryLabel,
  cashFlowEditableCategories,
  cashFlowIdFromHistoryRow,
  isCashFlowHistoryRow,
  isDeletedCashFlowRow,
  type HistoryCashFlowRow,
} from "./historyCashFlows";
import { setOwnLedgerCashFlowDeleted, updateOwnLedgerCashFlow } from "./ownLedgerClient";
import { showToast } from "./toast";

const MODAL_ID = "sfe-history-cash-edit-modal";

function closeModal(): void {
  document.getElementById(MODAL_ID)?.classList.remove("sfe-modal-open");
}

function formatUsd(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(amount);
}

function signedAmount(txn: HistoryCashFlowRow): number {
  const amount = Math.abs(Number(txn.avg_price) || 0);
  return txn.cash_flow_direction === "out" ? -amount : amount;
}

function modalTitle(txn: HistoryCashFlowRow): string {
  const prefix = isDeletedCashFlowRow(txn) ? "Deleted cash" : "Edit cash";
  return `${prefix} · ${formatUsd(signedAmount(txn))} · ${txn.trade_date ?? "—"}`;
}

/**
 * Edit category and comment on an own-ledger cash flow (History L).
 */
export function openHistoryCashFlowEditModal(
  txn: HistoryCashFlowRow,
  options: { onSaved?: () => void | Promise<void> } = {},
): void {
  const cashFlowId = cashFlowIdFromHistoryRow(txn);
  if (!cashFlowId) {
    showToast("This cash row cannot be edited.", "error");
    return;
  }

  const direction = txn.cash_flow_direction === "out" ? "out" : "in";
  const categories = cashFlowEditableCategories(direction);

  let overlay = document.getElementById(MODAL_ID);
  if (!overlay) {
    overlay = document.createElement("div");
    overlay.id = MODAL_ID;
    overlay.className = "sfe-modal-overlay";
    overlay.innerHTML = `
      <div class="sfe-modal sfe-history-cash-edit-modal" role="dialog" aria-labelledby="sfe-history-cash-edit-title">
        <div class="sfe-modal-header">
          <h3 id="sfe-history-cash-edit-title"></h3>
          <button type="button" class="sfe-modal-close" data-action="history-cash-edit-close" aria-label="Close">✕</button>
        </div>
        <p class="muted small sfe-history-cash-edit-lead" data-history-cash-edit-lead></p>
        <form class="sfe-history-cash-edit-form" data-history-cash-edit-form>
          <label class="sfe-modal-field">Type
            <select name="category" required data-history-cash-category></select>
          </label>
          <label class="sfe-modal-field">Comment
            <textarea name="note" rows="3" placeholder="Optional note (e.g. Q1 estimated tax)" data-history-cash-note></textarea>
          </label>
          <p class="muted small sfe-history-cash-edit-hint">
            Mark IRS payments as <strong>Tax</strong>. Tax cash for Own Capital comes only from this cash ledger — not the Tax tab payment log.
          </p>
          <div class="sfe-modal-actions sfe-history-cash-edit-actions">
            <button type="button" class="ghost small sfe-history-cash-edit-delete" data-history-cash-delete></button>
            <span class="sfe-history-cash-edit-gap"></span>
            <button type="button" class="ghost small" data-action="history-cash-edit-cancel">Cancel</button>
            <button type="submit" class="small">Save</button>
          </div>
        </form>
      </div>`;
    document.body.appendChild(overlay);

    overlay.addEventListener("click", (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      if (target === overlay) {
        closeModal();
        return;
      }
      const action = target.closest<HTMLElement>("[data-action]")?.dataset.action;
      if (action === "history-cash-edit-close" || action === "history-cash-edit-cancel") {
        closeModal();
      }
    });
  }

  const title = overlay.querySelector("#sfe-history-cash-edit-title");
  const lead = overlay.querySelector("[data-history-cash-edit-lead]");
  const form = overlay.querySelector<HTMLFormElement>("[data-history-cash-edit-form]");
  const categorySelect = overlay.querySelector<HTMLSelectElement>("[data-history-cash-category]");
  const noteInput = overlay.querySelector<HTMLTextAreaElement>("[data-history-cash-note]");
  const deleteBtn = overlay.querySelector<HTMLButtonElement>("[data-history-cash-delete]");
  if (!form || !title || !lead || !categorySelect || !noteInput || !deleteBtn) return;

  const deleted = isDeletedCashFlowRow(txn);
  title.textContent = modalTitle(txn);
  lead.textContent = deleted
    ? `Cash flow #${cashFlowId} is deleted — it is left out of History totals and Own Capital.`
    : `Cash flow #${cashFlowId}. Category and comment are saved to your SF+ ledger.`;

  categorySelect.replaceChildren(
    ...categories.map((cat) => {
      const opt = document.createElement("option");
      opt.value = cat;
      opt.textContent = cashFlowCategoryLabel(cat);
      return opt;
    }),
  );
  const currentCategory = String(txn.cash_flow_category ?? "").trim().toLowerCase();
  categorySelect.value = categories.includes(currentCategory as (typeof categories)[number])
    ? currentCategory
    : categories[0];
  noteInput.value = String(txn.note ?? "").trim();

  const onSubmit = async (event: Event): Promise<void> => {
    event.preventDefault();
    const category = categorySelect.value;
    const note = noteInput.value.trim();
    try {
      await updateOwnLedgerCashFlow(cashFlowId, {
        category,
        note: note || null,
      });
      showToast("Cash flow updated.", "success");
      closeModal();
      await options.onSaved?.();
    } catch {
      showToast("Could not update cash flow.", "error");
    }
  };

  form.onsubmit = (event) => {
    void onSubmit(event);
  };

  const onDeleteToggle = async (): Promise<void> => {
    if (
      !deleted &&
      !(await confirmDialog({
        title: "Delete cash transaction?",
        message: `${formatUsd(signedAmount(txn))} on ${txn.trade_date ?? "—"} will be left out of History, the running cash total and Own Capital. It stays deleted through future imports.`,
        confirmLabel: "Delete",
        danger: true,
      }))
    ) {
      return;
    }
    try {
      await setOwnLedgerCashFlowDeleted(cashFlowId, !deleted);
      showToast(deleted ? "Cash flow restored." : "Cash flow deleted.", "success");
      closeModal();
      await options.onSaved?.();
    } catch {
      showToast(
        deleted ? "Could not restore cash flow." : "Could not delete cash flow.",
        "error",
      );
    }
  };

  deleteBtn.textContent = deleted ? "Restore" : "Delete";
  deleteBtn.classList.toggle("sfe-btn-danger", !deleted);
  deleteBtn.onclick = () => {
    void onDeleteToggle();
  };

  overlay.classList.add("sfe-modal-open");
  categorySelect.focus();
}

export function openHistoryCashFlowEditModalFromTxn(
  txn: unknown,
  options: { onSaved?: () => void | Promise<void> } = {},
): void {
  if (!isCashFlowHistoryRow(txn as HistoryCashFlowRow)) {
    showToast("This row is not a cash flow.", "error");
    return;
  }
  openHistoryCashFlowEditModal(txn as HistoryCashFlowRow, options);
}
